import type { ReactNode } from 'react'

import { useGsap } from '@/hooks/use-gsap'
import { SplitWords } from '@/components/generals/split-words'
import { corePrinciples } from '@/constants/about'

export const AboutPhilosophy = (): ReactNode => {
    const scope = useGsap<HTMLElement>(({ gsap, select, scope: root, reduced }) => {
        if (reduced) return

        gsap.from(select('[data-philosophy-title] .reveal-mask > span'), {
            yPercent: 115,
            duration: 1.05,
            stagger: 0.06,
            ease: 'nuvea',
            scrollTrigger: { trigger: root, start: 'top 76%' }
        })

        gsap.from(select('[data-philosophy-copy] > *'), {
            y: 24,
            opacity: 0,
            duration: 0.9,
            stagger: 0.09,
            ease: 'nuvea',
            scrollTrigger: { trigger: root, start: 'top 72%' }
        })

        // Each principle's rule draws out before the copy settles beneath it.
        select('[data-philosophy-item]').forEach((item, index) => {
            const trigger = { trigger: item, start: 'top 88%' }

            gsap.from(item.querySelector('[data-philosophy-rule]'), {
                scaleX: 0,
                transformOrigin: 'left center',
                duration: 1,
                delay: index * 0.08,
                ease: 'nuvea',
                scrollTrigger: trigger
            })

            gsap.from(item.querySelectorAll('[data-philosophy-text]'), {
                y: 20,
                opacity: 0,
                duration: 0.85,
                stagger: 0.07,
                delay: index * 0.08 + 0.1,
                ease: 'nuvea',
                scrollTrigger: trigger
            })
        })
    })

    return (
        <section
            ref={scope}
            id='philosophy'
            className='bg-shell py-24 md:py-32'
        >
            <div className='shell-x mx-auto w-full max-w-[1760px]'>
                <SplitWords
                    data-philosophy-title=''
                    as='h2'
                    lines={['How we decide']}
                    className='type-display text-ink'
                />

                <div className='mt-10 grid grid-cols-1 gap-8 md:mt-14 md:grid-cols-[10rem_1fr] md:gap-12 lg:grid-cols-[14rem_minmax(0,56ch)_1fr]'>
                    <p className='type-label h-fit w-fit rounded-full bg-sage px-3 py-1.5 text-ink'>
                        Three rules
                    </p>

                    <div data-philosophy-copy='' className='flex flex-col gap-6'>
                        <p className='type-lead text-ink'>
                            Skin is already good at repairing itself. Most products interrupt that work rather than
                            support it, and then sell you the next product to fix the
                            interruption.
                        </p>

                        <p className='type-copy text-slate-soft'>
                            So we hold ourselves to three rules, and every formulation decision has to survive all
                            three:
                        </p>
                    </div>
                </div>

                <ul className='mt-16 grid grid-cols-1 gap-10 md:mt-24 md:grid-cols-3 md:gap-8 lg:gap-16'>
                    {corePrinciples.map((principle) => (
                        <li key={principle.number} data-philosophy-item='' className='relative pt-5'>
                            <span
                                data-philosophy-rule=''
                                aria-hidden='true'
                                className='absolute inset-x-0 top-0 block h-px bg-stone-line'
                            />

                            <div className='flex items-baseline justify-between gap-4'>
                                <h3 data-philosophy-text='' className='type-body text-ink'>
                                    {principle.title}
                                </h3>

                                <span
                                    data-philosophy-text=''
                                    className='type-small text-slate-mute tabular-nums'
                                >
                                    {principle.number}
                                </span>
                            </div>

                            <p
                                data-philosophy-text=''
                                className='type-copy mt-5 max-w-[44ch] text-slate-soft'
                            >
                                {principle.body}
                            </p>
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    )
}
